import { validateNewPassword } from './password';

// HTML pattern uses Unicode-sets mode; escape literal hyphens even at the class end.
export const USERNAME_PATTERN = /^[a-zA-Z0-9][a-zA-Z0-9._\-]{2,127}$/.source;

export interface FormIssue {
  summary: string;
  detail?: string;
  field?: string;
}

// Unknown server messages remain evidence; do not guess which field they describe.
export function operationIssue(error: unknown, summary: string): FormIssue {
  const detail = error instanceof Error ? error.message.trim() : '';
  return { summary, ...(detail && detail !== summary ? { detail } : {}) };
}

export function passwordIssue(value: FormDataEntryValue | null, field: string): FormIssue | null {
  try {
    validateNewPassword(value);
    return null;
  } catch (error) {
    return { summary: (error as Error).message, field };
  }
}

type ConstraintFlags = Pick<
  ValidityState,
  | 'valueMissing'
  | 'patternMismatch'
  | 'tooShort'
  | 'tooLong'
  | 'typeMismatch'
  | 'rangeUnderflow'
  | 'rangeOverflow'
  | 'badInput'
>;
export function constraintMessage(flags: Partial<ConstraintFlags>, label: string): string {
  if (flags.valueMissing) return `Nhập ${label.toLocaleLowerCase('vi-VN')}.`;
  if (flags.patternMismatch) return `${label} không đúng định dạng. Xem hướng dẫn bên dưới ô nhập.`;
  if (flags.tooShort) return `${label} quá ngắn. Kiểm tra hướng dẫn bên dưới ô nhập.`;
  if (flags.tooLong) return `${label} quá dài. Hãy rút ngắn nội dung.`;
  return `Kiểm tra lại ${label.toLocaleLowerCase('vi-VN')}.`;
}

// noValidate on the form lets one deterministic summary replace browser-language bubbles.
export function firstConstraintIssue(form: HTMLFormElement): FormIssue | null {
  for (const element of Array.from(form.elements)) {
    if (!(
      element instanceof HTMLInputElement ||
      element instanceof HTMLSelectElement ||
      element instanceof HTMLTextAreaElement
    ))
      continue;
    if (!element.willValidate || element.validity.valid) continue;
    return {
      summary: constraintMessage(element.validity, element.dataset.fieldLabel || element.name),
      field: element.id || undefined,
    };
  }
  return null;
}

export function errorDescription(
  helpId: string | undefined,
  issue: FormIssue | null,
  summaryId: string,
  field: string,
): string | undefined {
  return (
    [helpId, issue?.field === field ? summaryId : undefined].filter(Boolean).join(' ') || undefined
  );
}
